import { FC, useEffect, useState } from 'react';
import { Avatar, Button, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  DATE_FORMAT,
  FULL_DATE_FORMAT,
  FULL_DATE_FORMAT_2,
  TIME_FORMAT
} from 'constants/dates';
import {
  DOCTOR_HOW_IT_WORKS_PAGES,
  encodeToBase64,
  JOIN_TIMER_SECONDS
} from '@medicusunion/front-utils';
import { useNavigate } from 'react-router-dom';
import { useCallButton } from 'hooks';
import {
  LegacyAppointment,
  fetchNearestAppointment,
  openRoom as openRoomRest
} from 'services/api/appointments';

import { getFileUrl } from 'utils/file';
import {
  DateItem,
  StyledAvatarContainer,
  StyledItem,
  StyledItemContent,
  StyledTimeContainer
} from './styled';
import { CallButton } from 'pages/AppointmentsDetails/components';
import { showRestError } from 'utils/showErrorMessage';
import { tokenStorage, refreshTokenStorage } from 'utils/storage';
import useIsMobile from 'hooks/useIsMobile';

const { Title, Text } = Typography;

const AppointmentItem: FC = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [nearestData, setNearestData] = useState<LegacyAppointment | null>(
    null
  );

  useEffect(() => {
    fetchNearestAppointment()
      .then(setNearestData)
      .catch(() => setNearestData(null));
  }, []);

  const openRoom = (id: number | string) => {
    openRoomRest(id)
      .then(res => {
        const token = encodeToBase64(tokenStorage.get());
        const refreshToken = encodeToBase64(refreshTokenStorage.get());

        if (res.redirect_url) {
          const link = `${res.redirect_url}/home/${res.meetUrl}?t=${token}&r=${refreshToken}&lang=${i18n.language}`;

          window.location.href = link;
        }
      })
      .catch(err => showRestError(err));
  };

  const seconds = useCallButton(
    dayjs(nearestData?.date).format(FULL_DATE_FORMAT)
  );

  const showJoinCall = seconds <= JOIN_TIMER_SECONDS;

  return (
    <div>
      {!!nearestData && <Title level={4}>{t('dashboard.upcoming')}</Title>}
      <StyledItem>
        <StyledItemContent>
          {nearestData ? (
            <Space direction="vertical" size={16}>
              <StyledAvatarContainer>
                <Avatar
                  src={getFileUrl(nearestData.patient.avatar)}
                  size={42}
                  style={{ marginRight: 16 }}
                />
                <div>
                  <Title level={5} style={{ display: 'block', margin: 0 }}>
                    {nearestData.patient.first_name}{' '}
                    {nearestData.patient.last_name}
                  </Title>
                  <Text style={{ fontSize: 12 }}>{nearestData.plan.name}</Text>
                </div>
              </StyledAvatarContainer>
              {showJoinCall ? (
                <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                  <StyledTimeContainer>
                    <Title style={{ margin: 0, fontSize: 14 }}>
                      {dayjs(nearestData.date).format(FULL_DATE_FORMAT_2)}
                    </Title>
                  </StyledTimeContainer>
                  <CallButton
                    fullWidth
                    date={dayjs(nearestData.date).format(FULL_DATE_FORMAT)}
                    onJoinClick={() => {
                      openRoom(nearestData.appointment_id);
                    }}
                  />
                </Space>
              ) : (
                <Space>
                  <StyledTimeContainer>
                    <DateItem>
                      <CalendarOutlined />
                      <Title style={{ margin: 0, fontSize: 14 }}>
                        {dayjs(nearestData.date).format(DATE_FORMAT)}
                      </Title>
                    </DateItem>
                    <DateItem>
                      <ClockCircleOutlined />
                      <Title style={{ margin: 0, fontSize: 14 }}>
                        {dayjs(nearestData.date).format(TIME_FORMAT)}
                      </Title>
                    </DateItem>
                  </StyledTimeContainer>
                </Space>
              )}
            </Space>
          ) : (
            <div>
              <Title level={4}>{t('dashboard.why_medicus')}</Title>
              <Button
                type="primary"
                size="large"
                onClick={() =>
                  navigate(
                    `/how-it-works/${DOCTOR_HOW_IT_WORKS_PAGES.DASHBOARD.key}`
                  )
                }
              >
                {t('dashboard.see_how_it_works')}
              </Button>
            </div>
          )}
        </StyledItemContent>
      </StyledItem>
    </div>
  );
};

export default AppointmentItem;
